// Diagram display preferences, kept per browser.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FocusDepth } from './focus';
import type { PropertyMode } from './graph';
import type { LayoutKind, Spacing } from './layouts';

export type EdgeStyle = 'square' | 'smooth';

interface DiagramSettings {
  properties: PropertyMode;
  collapseJoins: boolean;
  /** How cards are arranged; each arrangement keeps its own positions. */
  layout: LayoutKind;
  /** How far apart cards are placed (grows with the number of cards too). */
  spacing: Spacing;
  /** Focus: how many steps around the focused entity to show ('all': everything connected to it). */
  focusDepth: FocusDepth;
  /** Relationship lines: right-angled, or smooth curves. */
  edgeStyle: EdgeStyle;
  minimap: boolean;
  /** Timeline: fade cards that did not change. */
  dimUnchanged: boolean;
  /** Sketches: color what changed from the model the sketch started from. */
  sketchChanges: boolean;
  /** Sketches: show the JSON editor beside the diagram. */
  sketchJson: boolean;
  setProperties: (mode: PropertyMode) => void;
  setLayout: (layout: LayoutKind) => void;
  setSpacing: (spacing: Spacing) => void;
  setFocusDepth: (depth: FocusDepth) => void;
  setEdgeStyle: (style: EdgeStyle) => void;
  toggleCollapseJoins: () => void;
  toggleMinimap: () => void;
  toggleDimUnchanged: () => void;
  toggleSketchChanges: () => void;
  toggleSketchJson: () => void;
}

export const useDiagramSettings = create<DiagramSettings>()(
  persist(
    (set) => ({
      properties: 'all',
      collapseJoins: true,
      layout: 'right',
      spacing: 'normal',
      focusDepth: 'all',
      edgeStyle: 'square',
      minimap: true,
      dimUnchanged: false,
      sketchChanges: true,
      sketchJson: false,
      setProperties: (properties) => set({ properties }),
      setLayout: (layout) => set({ layout }),
      setSpacing: (spacing) => set({ spacing }),
      setFocusDepth: (focusDepth) => set({ focusDepth }),
      setEdgeStyle: (edgeStyle) => set({ edgeStyle }),
      toggleCollapseJoins: () => set((s) => ({ collapseJoins: !s.collapseJoins })),
      toggleMinimap: () => set((s) => ({ minimap: !s.minimap })),
      toggleDimUnchanged: () => set((s) => ({ dimUnchanged: !s.dimUnchanged })),
      toggleSketchChanges: () => set((s) => ({ sketchChanges: !s.sketchChanges })),
      toggleSketchJson: () => set((s) => ({ sketchJson: !s.sketchJson })),
    }),
    {
      name: 'mv-diagram',
      version: 1,
      // Version 0 kept a focus depth of 1 or 2 steps; focus now shows everything connected unless asked.
      migrate: (state, version) => (version < 1 ? { ...(state as object), focusDepth: 'all' } : state) as DiagramSettings,
    },
  ),
);
